import * as Promise from 'bluebird';
import * as status from 'http-status';
import { getUserId } from '../../services/jwt/jwtService';
import { isFetching } from '../componentActions';
import * as messageAlert from '../messageAlertAction';
import { I18n } from 'react-redux-i18n-lite';
import * as classroomGroupActions from './classroomGroupActions';
import { ASSET_TYPES, ROUTES } from '../../const';
import routerHistory from '../../routerHistory';
import * as classroomApi from '../../services/api/classroomApi';
import { getLogger } from '../../services/logger';
import * as assetsService from '../../services/assets/assetsService';
import { REDUCER } from '../../reducers/reducersConst';
import * as formService from '../../services/form/formService';
import { ANALYTICS_CONTEXT, ANALYTICS_EVENT } from '../../types/analytics';
import * as analyticsApi from '../../services/api/analyticsApi';
import { isMobileViewport } from '../../utils/mobileUtils';

const debug = getLogger('actions:classroom');

let crossPlatformSpecialization;
export const configure = injectedCrossPlatformSpecialization => {
  crossPlatformSpecialization = injectedCrossPlatformSpecialization;
};

export const setActiveClassroomData = (activeClassrooms = []) => {
  return {
    type: REDUCER.CLASSROOM.SET_ACTIVE_CLASSROOM_DATA,
    activeClassrooms
  };
};

export const setArchivedClassroomData = (archivedClassrooms = []) => {
  return {
    type: REDUCER.CLASSROOM.SET_ARCHIVED_CLASSROOM_DATA,
    archivedClassrooms
  };
};

export const setAddModalState = (addModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_ADD_MODAL_STATE,
    addModalIsOpen
  };
};

export const setEnrollModalState = (enrollModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_ENROLL_MODAL_STATE,
    enrollModalIsOpen
  };
};

export const setSubmitTaskModalState = (submitTaskDataIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_SUBMIT_TASK_DATA_STATE,
    submitTaskDataIsOpen
  };
};

export const sendTaskData = (sendTaskDataIsSending = false) => {
  return {
    type: REDUCER.CLASSROOM.SEND_SUBMIT_TASK_DATA_STATE,
    sendTaskDataIsSending
  };
};

export const setEditRoleModalState = (editRoleModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_EDIT_ROLE_MODAL_STATE,
    editRoleModalIsOpen
  };
};

export const setNotCanUploadFilesModalIsOpen = (notCanUploadFilesModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_NOT_CAN_UPLOAD_FILES,
    notCanUploadFilesModalIsOpen
  };
};

export const setUnenrollModalState = (unenrollModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_UNENROLL_MODAL_STATE,
    unenrollModalIsOpen
  };
};

export const setImportCSVModalState = (importCSVModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_IMPORT_MODAL_STATE,
    importCSVModalIsOpen
  };
};

export const setFailImportCSVModalState = (failImportCSVModalIsOpen = false) => {
  return {
    type: REDUCER.CLASSROOM.SET_FAIL_IMPORT_MODAL_STATE,
    failImportCSVModalIsOpen
  };
};

export const setFailImportStudentsErrors = (studentsErrors = []) => {
  return {
    type: REDUCER.CLASSROOM.SET_FAIL_IMPORT_STUDENTS_ERRORS,
    studentsErrors
  };
};

export const setStudentsErrorsAndOpenModal = (studentsErrors = []) => {
  return dispatch => {
    dispatch(setFailImportStudentsErrors(studentsErrors));
    dispatch(setFailImportCSVModalState(true));
  };
};

export const saveCSVFile = (csvFile = null) => {
  return {
    type: REDUCER.CLASSROOM.SET_CSV_FILE_UPLOADED,
    csvFile
  };
};

export const setArchiveClassroomData = (archiveModalClassroomData = null) => {
  return {
    type: REDUCER.CLASSROOM.SET_ARCHIVE_MODAL_DATA,
    archiveModalClassroomData
  };
};

export const setFetchingClassrooms = (fetching = false) => {
  return {
    type: REDUCER.CLASSROOM.FETCHING,
    fetching
  };
};

export const fetchActiveClassrooms = userId => {
  return {
    type: REDUCER.CLASSROOM.FETCH_ACTIVE_CLASSROOMS,
    payload: { userId }
  };
};

export const setFetchingParticipants = (fetchingParticipants = false) => {
  return {
    type: REDUCER.CLASSROOM.FETCHING_PARTICIPANTS,
    fetchingParticipants
  };
};

export const setClassroomParticipants = (participants = null) => {
  return {
    type: REDUCER.SEL_CLASSROOM.SET_CLASSROOM_PARTICIPANTS,
    participants
  };
};

export const setClassroomVideocall = (videocall = {}) => {
  return {
    type: REDUCER.SEL_CLASSROOM.SET_VIDEOCALL_INFO,
    videocall
  };
};

export const setStudentListState = listState => {
  return {
    type: REDUCER.SEL_CLASSROOM.SET_STUDENT_LIST_STATE,
    listState
  };
};

export const fetchClassroomInfo = (classroomId = '') => {
  return {
    type: REDUCER.CLASSROOM.FETCH_CURRENT_CLASSROOM,
    classroomId
  };
};

export const setClassroomInfo = (classroom = {}) => {
  return {
    type: REDUCER.SEL_CLASSROOM.SET_CLASSROOM_INFO,
    classroom
  };
};

export const setClassCodePopupOpen = (classCodeIsOpen = false) => {
  return (dispatch, getState) => {
    dispatch({
      type: REDUCER.SEL_CLASSROOM.SET_CLASSCODE_POPUP_STATE,
      classCodeIsOpen
    });
    if (!classCodeIsOpen) {
      return;
    }
    const classroom = getState().classrooms.selectedClassroom.classroom;
    if (!classroom || !classroom.id) {
      return;
    }
    analyticsApi.trackAnalyticsEvent({
      event: ANALYTICS_EVENT.VIEW_CLASS_CODE,
      classId: classroom.id,
      // ClassCodeReveal shows a bottom sheet on mobile and a modal on desktop
      context: isMobileViewport() ? ANALYTICS_CONTEXT.CLASS_CODE_SHEET : ANALYTICS_CONTEXT.CLASS_CODE_POPUP,
      status: 'success'
    });
  };
};

export const setClassroomGroups = (groups = []) => {
  return {
    type: REDUCER.SEL_CLASSROOM.SET_CLASSROOM_GROUPS,
    groups
  };
};

export const setSelectedStudentProfile = selectedStudent => {
  return {
    type: REDUCER.STUDENT.SET_SELECTED_PROFILE,
    selectedStudent
  };
};

export const getSelectedStudentProfile = selectedStudent => {
  return dispatch => {
    dispatch({
      type: REDUCER.STUDENT.SET_SELECTED_PROFILE,
      selectedStudent
    });
    return selectedStudent;
  };
};

export const getActiveClassroomData = () => {
  return (dispatch, getState) => {
    let store = getState();
    let userId = getUserId(store);
    if (userId) {
      return dispatch(fetchActiveClassrooms(userId));
    }
  };
};

export const getArchivedClassroomData = () => {
  return (dispatch, getState) => {
    let store = getState();
    let userId = getUserId(store);
    dispatch(retrieveArchivedClassrooms(userId));
  };
};

export const retrieveArchivedClassrooms = userId => {
  return dispatch => {
    if (userId) {
      dispatch(setFetchingClassrooms(true));
      return classroomApi.getArchiveClassrooms(userId).then(async res => {
        await dispatch(setArchivedClassroomData(res.data));
        dispatch(setFetchingClassrooms(false));
        return res;
      });
    }
    routerHistory.push(ROUTES.HOME);
  };
};

export const getClassroomInfo = (classroomId = '') => {
  return async (dispatch, getState) => {
    let state = getState();
    let classroomState = state.classrooms.selectedClassroom.classroom;
    if (!classroomState.id || classroomState.id !== classroomId) {
      await dispatch(fetchClassroomInfo(classroomId));
    }
    return Promise.resolve(classroomState);
  };
};

export const editClassroomWithPicture = (classroomName = '', picture, classroomId) => {
  return dispatch => {
    if (classroomName) {
      return dispatch(
        editClassroom({
          picture,
          name: classroomName,
          id: classroomId
        })
      );
    }
  };
};

export const editClassroomWithAsset = (classroomName = '', image, classroomId) => {
  return async (dispatch, getState) => {
    let uploadAsset = '';
    if (classroomName) {
      const finalFileName = `${new Date().getTime()}.png`;
      const customerId = getState().account.configuration.customerId;
      const user = getState().account.user;
      const assetMetadata = {
        fileName: finalFileName,
        userId: user._id,
        customerId: customerId,
        assetType: ASSET_TYPES.CLASSROOM_IMAGE
      };
      uploadAsset = await assetsService
        .uploadAsset(image, assetMetadata)
        .then(async asset => {
          return dispatch(
            editClassroom({
              asset: asset.uuid,
              name: classroomName,
              id: classroomId
            })
          );
        })
        .catch(err => {
          debug.error('[ClassroomActions] addClassroom %s', err);
        });
      return uploadAsset;
    }
  };
};

export const editClassroom = classroom => {
  return (dispatch, getState) => {
    const activeClassroomsOldValue = getState().classrooms.activeClassrooms;
    return classroomApi
      .editClassroom(classroom)
      .then(res => {
        const activeClassrooms = activeClassroomsOldValue.map(item =>
          item.id === res.data.id ? Object.assign(item, res.data) : item
        );
        dispatch(setActiveClassroomData(activeClassrooms));
        return res;
      })
      .catch(err => {
        console.error(err);
        dispatch(messageAlert.error(I18n.t('classroom.error.editClassroom')));
      });
  };
};

export const addClassroomWithPicture = (classroomName = '', defaultGroups = true, picture) => {
  return (dispatch, getState) => {
    if (classroomName) {
      return dispatch(
        addClassroom({
          picture,
          name: classroomName,
          createDefaultGroups: defaultGroups,
          context: ANALYTICS_CONTEXT.CLASSROOM_CREATE
        })
      );
    }
  };
};

export const addClassroomWithAsset = (classroomName = '', defaultGroups = true, image) => {
  return async (dispatch, getState) => {
    let uploadAsset = '';
    if (classroomName) {
      const finalFileName = `${new Date().getTime()}.png`;
      const customerId = getState().account.configuration.customerId;
      const assetMetadata = {
        fileName: finalFileName,
        customerId: customerId,
        assetType: ASSET_TYPES.CLASSROOM_IMAGE
      };
      uploadAsset = await assetsService
        .uploadAsset(image, assetMetadata)
        .then(asset => {
          return dispatch(
            addClassroom({
              asset: asset.uuid,
              name: classroomName,
              createDefaultGroups: defaultGroups,
              context: ANALYTICS_CONTEXT.CLASSROOM_CREATE
            })
          );
        })
        .catch(err => {
          debug.error('[ClassroomActions] addClassroom %s', err);
        });
    }
    return uploadAsset;
  };
};

export const getClassroomVideocall = classroomId => {
  return (dispatch, getState) => {
    return classroomApi
      .getVideocallInfo(classroomId)
      .then(res => {
        const videocallInfo = res.data;
        dispatch(setClassroomVideocall(videocallInfo));
        return videocallInfo;
      })
      .catch(err => {
        debug.error('[ClassroomActions] getClassroomVideocall %s', err);
      });
  };
};

export const updateOrCreateVideocallInfo = (classroomId, videocallUrl) => {
  return (dispatch, getState) => {
    return classroomApi
      .updateOrCreateVideocallInfo(classroomId, videocallUrl)
      .then(res => {
        const videocallInfo = res.data;
        dispatch(setClassroomVideocall(videocallInfo));
        return videocallInfo;
      })
      .catch(err => {
        debug.error('[ClassroomActions] updateOrCreateVideocallInfo %s', err);
      });
  };
};

export const addClassroom = classroom => {
  return (dispatch, getState) => {
    const activeClassroomsOldValue = getState().classrooms.activeClassrooms;
    return classroomApi
      .addClassroom(classroom)
      .then(res => {
        const classroomCreated = res.data;
        classroomCreated.position = activeClassroomsOldValue.length + 1;
        const activeClassrooms = activeClassroomsOldValue.concat([classroomCreated]);
        dispatch(setActiveClassroomData(activeClassrooms));
        return res;
      })
      .catch(err => {
        console.error(err);
        dispatch(messageAlert.error(I18n.t('classroom.error.addClassroom')));
      });
  };
};

export const enrollClassroom = (classCode = '') => {
  return (dispatch, getState) => {
    let store = getState();
    let userId = getUserId(store);
    if (userId && classCode) {
      dispatch(isFetching(true));
      return classroomApi
        .enrollClassroom(userId, { classCode: classCode })
        .then(res => {
          dispatch(getActiveClassroomData());
          dispatch(isFetching(false));
          return res;
        })
        .catch(err => {
          if (err.response.status === status.NOT_FOUND) {
            dispatch(messageAlert.error(I18n.t('classroom.error.classCode')));
          }
          dispatch(isFetching(false));
        });
    }
  };
};

export const getClassroomPositions = (classrooms = []) => {
  let idPositions = [];
  for (let i = 0; i < classrooms.length; i++) {
    let idPosition = {};
    idPosition.id = classrooms[i].sectionId;
    idPosition.position = i + 1;
    idPositions.push(idPosition);
  }

  return idPositions;
};

export const setClassroomStudentsCount = (idClassroom, studentsCount) => {
  return (dispatch, getState) => {
    let store = getState();
    let activeClassrooms = store.classrooms.activeClassrooms;
    let activeClassroom = activeClassrooms.find(classroom => classroom.id === idClassroom);
    activeClassroom.studentsCount =
      typeof activeClassroom.studentsCount === 'number' ? activeClassroom.studentsCount + studentsCount : studentsCount;
    dispatch(setActiveClassroomData(activeClassrooms));
  };
};

export const changePositionClassroom = newClassroomOrder => {
  return (dispatch, getState) => {
    let store = getState();
    let userId = getUserId(store);
    if (userId) {
      let idPositions = getClassroomPositions(newClassroomOrder);
      return classroomApi.changeClassroomsOrder(userId, idPositions).then(res => {
        let activeClassrooms = store.classrooms.activeClassrooms;
        activeClassrooms = activeClassrooms.map(classroom => {
          const sectionFound = res.data.find(function(section) {
            return section.SectionId === classroom.id;
          });
          if (sectionFound) {
            classroom.position = sectionFound.position;
          }
          return classroom;
        });
        dispatch(setActiveClassroomData(activeClassrooms));
      });
    }
  };
};

export const archiveClassroom = (classId = '') => {
  return (dispatch, getState) => {
    if (classId) {
      const store = getState();
      const activeClassroomsOldValue = getState().classrooms.activeClassrooms;
      const activeClassrooms = activeClassroomsOldValue.filter(item => item.id !== classId);
      dispatch(setActiveClassroomData(activeClassrooms));
      return classroomApi
        .archiveClassroom(classId, ANALYTICS_CONTEXT.CLASSROOM_ARCHIVE)
        .then(() => {
          dispatch(crossPlatformSpecialization.archiveClassroomPostAction(classId, store.account.user._id));
          return Promise.resolve();
        })
        .catch(() => {
          dispatch(setActiveClassroomData(activeClassroomsOldValue));
          dispatch(messageAlert.error(I18n.t('classroom.error.archiveClassroom')));
        });
    }
  };
};

export const recoverClassroom = classroom => {
  return (dispatch, getState) => {
    const archivedClassroomsOldValue = getState().classrooms.archivedClassrooms;
    const activeClassroomsOldValue = getState().classrooms.activeClassrooms;
    const archivedClassrooms = archivedClassroomsOldValue.filter(item => item.id !== classroom.id);
    if (classroom) {
      dispatch(setArchivedClassroomData(archivedClassrooms));
      return classroomApi
        .recoverClassroom(classroom.id)
        .then(() => {
          dispatch(messageAlert.success(I18n.t('common.recoveredClassroom')));
          const activeClassrooms = activeClassroomsOldValue.concat([classroom]);
          dispatch(setActiveClassroomData(activeClassrooms));
        })
        .catch(() => {
          dispatch(setActiveClassroomData(archivedClassroomsOldValue));
          dispatch(messageAlert.error(I18n.t('classroom.error.recoverClassroom')));
        });
    }
  };
};

export const getClassroomParticipants = (classId = '') => {
  return dispatch => {
    if (classId) {
      return dispatch(classroomGroupActions.getParticipantsWithStatus(classId));
    }
  };
};

export const getClassroomGroups = (classId = '') => {
  return dispatch => {
    if (classId) {
      return dispatch(classroomGroupActions.getClassroomGroups(classId));
    }
  };
};

export const updateStudents = (student = {}, classroom = {}) => {
  return (dispatch, getState) => {
    let state = getState();
    let group = state.selectedGroup.group;

    if (classroom && !group.isEveryone) {
      dispatch(classroomGroupActions.getStudentsFromGroup(classroom.id));
    } else {
      dispatch(getClassroomParticipants(classroom.id));
    }
  };
};

export const getStudentsImportedFromCSV = (csvFile = null, sectionId, classroomId) => {
  return dispatch => {
    if (csvFile) {
      dispatch(isFetching(true));

      let language = localStorage.smartAuthLanguage;
      return classroomApi
        .getImportedStudents(sectionId, formService.getFormDataFromFile(csvFile, language))
        .then(res => {
          dispatch(saveCSVFile());
          dispatch(isFetching(false));

          if (res.data && res.data.errors && res.data.errors.length) {
            dispatch(setStudentsErrorsAndOpenModal(res.data.errors));
          }
          dispatch(getClassroomParticipants(classroomId));

          return res;
        })
        .catch(() => {
          dispatch(saveCSVFile());
          dispatch(messageAlert.error(I18n.t('classroom.error.importStudents')));
          dispatch(isFetching(false));
        });
    }
  };
};

export const unenrollStudent = (userId, classroomId) => {
  return dispatch => {
    if (userId && classroomId) {
      return classroomApi.unenrollStudent(userId, classroomId).catch(() => {
        dispatch(messageAlert.error(I18n.t('api.error.500')));
      });
    }
  };
};

export const unenrollTeacher = (userId, classroomId) => {
  return dispatch => {
    if (userId && classroomId) {
      return classroomApi
        .unenrollStudent(userId, classroomId)
        .then(() => {
          dispatch(messageAlert.success(I18n.t('common.userUnenrolled')));
        })
        .catch(() => {
          dispatch(messageAlert.error(I18n.t('api.error.500')));
        });
    }
  };
};

export const setActiveTab = activeClassroomTab => {
  return {
    type: REDUCER.CLASSROOM.SET_ACTIVE_TAB,
    activeClassroomTab
  };
};
